import React, { useState, useEffect, useRef } from 'react';
import { reverseGeocode } from '../../utils/geocoding';
import './SingleReportHelper.css';
import moment from 'moment';
import DefaultPFP from '../../Media/Icons/DefaultPFP.webp';
import UnknownAuthority from '../../Media/Icons/UnknownAuthority.webp';
import Anonymous from '../../Media/Icons/anonymous.png';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BigPlayButton, ControlBar, PlaybackRateMenuButton, Player } from 'video-react';
import { useUser } from '../../UserContext';
import { BACKEND_URL, MEDIA_BASE_URL, profilePicUrl } from '../../config';
import { useParams, useNavigate } from 'react-router-dom';
import { Gauge, X, AlertCircle, Lock, Hash, Pencil, CheckCircle2 } from 'lucide-react';
import RiskoMeter from '../RiskoMeter/RiskoMeter';
import SophisticatedAddress from '../Common/SophisticatedAddress';
import { formatCustomDistanceToNow } from '../../utils/timeUtils';
import { useData } from '../../DataContext';

// Geocoding now uses Nominatim (OpenStreetMap) via centralized utility in src/utils/geocoding.ts

export type Comments = {
	commentId: number;
	parentId: number | null;
	isAnonymous: boolean;
	user: {
		name: string;
		id: number;
		profilepicURL: string;
	};
	likeCount: number;
	createdAt: string;
	body: string;
};

export type AllPostDataType = {
	title: string;
	content: string;
	format: string;
	image_url: any;
	name: string;
	location: string;
	time: string;
	commentCount: number;
	comments: Comments[];
	likeCount: number;
	country: string | null;
	state: string | null;
	city: string | null;
	tags: string;
	profilepicURL: string;
	isLiked: number;
};

export type AuthoritySummary = {
	id: number;
	name: string;
	profilepicURL: string | null;
	organisation?: string | null;
};

export type ForwardHistoryEntry = {
	fromAuthorityId: number;
	fromAuthorityName: string;
	toAuthorityId: number;
	toAuthorityName: string;
	reason: string;
	date: string;
};

// One event in the report's chain of custody, built server-side so the website
// and the app show an identical timeline. Ordered oldest → newest.
export type ChainActor = {
	kind: 'user' | 'authority';
	id: number;
	name: string;
	profilepicURL: string | null;
	organisation?: string | null;
	isAnonymous: boolean;
};

export type ChainEvent = {
	kind: 'filed' | 'received' | 'progress' | 'forwarded' | 'closed' | 'reopened';
	date: string;
	reason: string;
	actor: ChainActor;
	target: ChainActor | null;
};

export type AllReportDataType = {
	id: number;
	title: string;
	content: string;
	format: string;
	image_url: any;
	name: string;
	location: string;
	time: string;
	commentCount: number;
	comments: Comments[];

	country: string | null;
	state: string | null;
	city: string | null;

	profilepicURL: string;
	internalURL: string | null;
	requiredFieldValues: { [key: string]: string };
	isAnonymous: boolean;
	address_parts?: string[];
	userId: number;

	// Forward-chain fields (populated from getReport response)
	forwardHistory?: ForwardHistoryEntry[];
	originalAuthority?: AuthoritySummary | null;
	currentAuthority?: AuthoritySummary | null;
	closedByAuthority?: AuthoritySummary | null;
	isClosed?: boolean;
	closedAt?: string | null;
	// Set when the reporter closed it themselves (no closedByAuthority then).
	closedByReporter?: boolean;
	closeReason?: string | null;

	// Full chain-of-custody timeline (filed → received → activated → forwarded →
	// closed → reopened …), replacing the old party-only rendering.
	reportChain?: ChainEvent[];
	inProgress?: boolean;
	status?: 'received' | 'in_progress' | 'closed';
	// Viewer-scoped: the asking authority's own bookkeeping number and whether
	// they're allowed to set one. The server decides both.
	reportNumber?: string | null;
	canSetReportNumber?: boolean;
};

export type User = {
	id: number;
	phoneNumber: number;
	name: string;
	isBlocked: boolean;
	gender: string;
	dob: string;
	email: string;
	country: string;
	state: string;
	district: string;
	pincode: number;
	likeCount: number;
	mediaCount: number;
	postCount: number;
	followerCount: number;
	reportCount: number;
	is_authority: boolean;
	profilepicURL: string;
};
interface CustomModalProps {
	show: boolean;
	handleClose: () => void;
	imageUrl: string;
}

const CustomModal: React.FC<CustomModalProps> = ({ show, handleClose, imageUrl }) => {
	if (!show) return null;

	return (
		<div className='custom-modal-overlay'>
			<div className='custom-modal-content'>
				<button className='custom-modal-close' onClick={handleClose}>
					×
				</button>
				<img src={imageUrl} alt='Full View' className='custom-modal-image' />
			</div>
		</div>
	);
};

const ReportPlaceholderSVG = () => (
	<div className="w-full h-full bg-gray-100 overflow-hidden relative flex items-center justify-center animate-shimmer-wave">
		<svg className="w-32 h-32 text-gray-300 z-10 drop-shadow-sm relative" fill="none" viewBox="0 0 24 24" stroke="currentColor">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</svg>
	</div>
);

const ReportImage = ({ src, alt, className }: { src: string; alt: string; className?: string }) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [hasError, setHasError] = useState(false);

	if (hasError)
		return (
			<div className={`${className || ''} aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100`}>
				<ReportPlaceholderSVG />
			</div>
		);

	return (
		<div
			className={`relative aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100 ${className || ''}`}
		>
			{!isLoaded && (
				<div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
					<ReportPlaceholderSVG />
				</div>
			)}
			<img
				src={src}
				alt={alt}
				loading='lazy'
				className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ease-in-out ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
				onLoad={() => setIsLoaded(true)}
				onError={() => setHasError(true)}
			/>
		</div>
	);
};

const ProfileImage = ({
	name,
	pfpURL,
	className,
	isAnonymous,
}: {
	name: string;
	pfpURL?: string | null;
	className?: string;
	isAnonymous?: boolean;
}) => {
	const isAuth = name.toLowerCase().includes('authority');
	const [hasError, setHasError] = useState(false);

	// Initial src decision
	const src = isAnonymous
		? Anonymous
		: !hasError && pfpURL && pfpURL !== 'null' && pfpURL !== ''
			? profilePicUrl(pfpURL)
			: isAuth
				? UnknownAuthority
				: DefaultPFP;

	return <img src={src} className={className} alt={name} onError={() => setHasError(true)} />;
};

// Per-event presentation: the node's colour/glyph, the role line under the
// actor's name, and the label above the timestamp.
//
// This replaced a party-per-row chain, which had no way to express an activation
// and represented a reopen by DELETING the close. Events say what happened, in
// order, so an authority that receives a report twice simply appears twice.
const CHAIN_EVENT_META: Record<
	ChainEvent['kind'],
	{ nodeClass: string; role: string; dateLabel: string }
> = {
	filed: { nodeClass: 'srh-chain-node--user', role: 'Reporter', dateLabel: 'Sent' },
	received: { nodeClass: 'srh-chain-node--auth', role: 'Received the report', dateLabel: 'Received' },
	progress: { nodeClass: 'srh-chain-node--progress', role: 'Started working on it', dateLabel: 'Started' },
	forwarded: { nodeClass: 'srh-chain-node--forwarded', role: 'Forwarded the report', dateLabel: 'Forwarded' },
	closed: { nodeClass: 'srh-chain-node--closer', role: 'Closed the report', dateLabel: 'Closed' },
	reopened: { nodeClass: 'srh-chain-node--reopened', role: 'Reopened the report', dateLabel: 'Reopened' },
};

const ChainEventIcon = ({ kind, size = 10 }: { kind: ChainEvent['kind']; size?: number }) => {
	const common = {
		width: size,
		height: size,
		viewBox: '0 0 24 24',
		fill: 'none',
		stroke: 'currentColor',
		strokeWidth: 3,
		strokeLinecap: 'round' as const,
		strokeLinejoin: 'round' as const,
	};
	switch (kind) {
		case 'closed':
			return (
				<svg {...common}>
					<polyline points='20 6 9 17 4 12' />
				</svg>
			);
		case 'progress':
			return (
				<svg {...common}>
					<polygon points='6 4 20 12 6 20 6 4' fill='currentColor' stroke='none' />
				</svg>
			);
		case 'reopened':
			return (
				<svg {...common}>
					<polyline points='1 4 1 10 7 10' />
					<path d='M3.51 15a9 9 0 1 0 2.13-9.36L1 10' />
				</svg>
			);
		case 'forwarded':
			return (
				<svg {...common}>
					<line x1='22' y1='2' x2='11' y2='13' />
					<polygon points='22 2 15 22 11 13 2 9 22 2' fill='currentColor' stroke='none' />
				</svg>
			);
		default:
			return null;
	}
};

const ReportChain = ({ data }: { data: AllReportDataType }) => {
	const events = data.reportChain || [];
	const [isExpanded, setIsExpanded] = useState(false);

	if (events.length === 0) return null;

	// Distinct authorities that have touched it, however many events each raised.
	const authorityCount = new Set(
		events.filter((e) => e.actor.kind === 'authority').map((e) => e.actor.id)
	).size;

	const fmt = (iso: string | null) => {
		if (!iso) return '—';
		try {
			return moment.utc(iso).local().format('DD MMM YYYY, HH:mm');
		} catch {
			return iso;
		}
	};

	const showCollapse = events.length > 3;
	const displayedEvents = showCollapse && !isExpanded ? events.slice(0, 3) : events;

	return (
		<div className='srh-chain-section'>
			<div className='srh-chain-header'>
				<span className='srh-chain-title'>Report Chain</span>
				<span className='srh-chain-subtitle'>
					{authorityCount} {authorityCount === 1 ? 'authority' : 'authorities'} involved
				</span>
			</div>
			<div style={{ position: 'relative' }}>
				<ol className='srh-chain-list'>
					{displayedEvents.map((event, idx) => {
						const isLast = idx === events.length - 1;
					const meta = CHAIN_EVENT_META[event.kind];
					// The final "received" is the authority holding it right now — give
					// it the highlighted current-node ring instead of the plain one.
					const nodeClass =
						event.kind === 'received' && isLast
							? 'srh-chain-node--current'
							: meta.nodeClass;
					const showsGlyph =
						event.kind === 'closed' ||
						event.kind === 'progress' ||
						event.kind === 'reopened' ||
						event.kind === 'forwarded';

					const role =
						event.kind === 'forwarded' && event.target
							? `Forwarded to ${event.target.name}`
							: event.kind === 'closed' && event.actor.kind === 'user'
								? 'Closed their report'
								: meta.role;
					const roleLine = event.actor.organisation
						? `${role} • ${event.actor.organisation.toUpperCase()}`
						: role;

					return (
						<li key={`${event.kind}-${event.date}-${idx}`} className='srh-chain-item'>
							{/* Vertical rail + node */}
							<div className='srh-chain-rail'>
								<div className={`srh-chain-node ${nodeClass}`}>
									<ProfileImage
										name={event.actor.name}
										pfpURL={event.actor.profilepicURL}
										className='srh-chain-pfp'
										isAnonymous={event.actor.isAnonymous}
									/>
								</div>
								{!isLast && <div className='srh-chain-line' />}
							</div>

							{/* Body */}
							<div className='srh-chain-body'>
								<div className='srh-chain-row'>
									<div className='srh-chain-name-block'>
										<span className='srh-chain-name inline-flex items-center gap-1.5'>
											<span>{event.actor.name}</span>
											{showsGlyph && (
												<span className={`srh-chain-icon-badge srh-chain-icon-badge--${event.kind}`}>
													<ChainEventIcon kind={event.kind} size={10} />
												</span>
											)}
										</span>
										<span className='srh-chain-role'>{roleLine}</span>
									</div>
									<div className='srh-chain-date-block'>
										<span className='srh-chain-date-label'>{meta.dateLabel}</span>
										<span className='srh-chain-date-value'>{fmt(event.date)}</span>
									</div>
								</div>
								{event.reason && (
									<div className='srh-chain-reason' title={event.reason}>
										<span className='srh-chain-reason-label'>Reason:</span> {event.reason}
									</div>
								)}
							</div>
						</li>
					);
				})}
				</ol>
				{showCollapse && !isExpanded && (
					<div className='srh-chain-blur' />
				)}
			</div>
			{showCollapse && (
				<button 
					className='srh-chain-toggle'
					onClick={() => setIsExpanded(!isExpanded)}
				>
					{isExpanded ? 'Show less' : `Show all ${events.length} events`}
				</button>
			)}
		</div>
	);
};

// Display-side cleanup of a stored extra-field key. Older caretaker templates
// baked the option hint into the label (e.g.
// "Complaint Category: Electrical/Mason/Carpentory/Other"); collapse that to a
// tidy "Complaint Category". Mirrors the Flutter app's prettyReportFieldLabel so
// the report reads the same on web and mobile.
const prettyFieldLabel = (key: string): string =>
	key.trim().toLowerCase().startsWith('complaint category') ? 'Complaint Category' : key.trim();

function SingleReportHelper() {
	const { user } = useUser();
	const { updatePostMetadata } = useData();
	const { reportId } = useParams<{ reportId?: string }>();
	const [AllReportData, set_AllReportData] = useState<AllReportDataType>();
	const [Oldest, set_Oldest] = useState<boolean>(false);
	const [CommentParentId, set_CommentParentId] = useState<number | null>(null);
	const [CommentName, set_CommentName] = useState<string>('');
	const [CommentBody, set_CommentBody] = useState<string>('');
	const [showModal, setShowModal] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const isMountedRef = useRef(true);
	const abortControllerRef = useRef<AbortController | null>(null);
	const [riskoMeterMediaId, setRiskoMeterMediaId] = useState<string | null>(null);
	const [openCommentDotsId, setOpenCommentDotsId] = useState<number | null>(null);
	const [isSortDropdownOpen, setSortDropdownOpen] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showCloseConfirm, setShowCloseConfirm] = useState(false);
	const [isClosingReport, setIsClosingReport] = useState(false);
	const [closeReasonDraft, setCloseReasonDraft] = useState('');
	const [reportGone, setReportGone] = useState(false);
	// Set when the API 403s this report (the viewer is not the reporter, an
	// involved authority, or an admin) — e.g. someone edited the id in the URL.
	const [accessDenied, setAccessDenied] = useState(false);
	const [toastMessage, setToastMessage] = useState<string | null>(null);

	const sortDropdownRef = useRef<HTMLDivElement>(null);
	const commentInputRef = useRef<HTMLInputElement>(null);
	const commentDropdownRef = useRef<HTMLDivElement>(null);

	// Report-number editing. Only shown to an authority that actually handled this
	// report (the server sets canSetReportNumber); the value is private to them.
	const [isEditingNumber, setIsEditingNumber] = useState(false);
	const [numberDraft, setNumberDraft] = useState('');
	const [isSavingNumber, setIsSavingNumber] = useState(false);
	const numberInputRef = useRef<HTMLInputElement>(null);

	// Helper to selectively update a report in the cached list views
	// without clearing the cache or losing scroll position.
	const patchReportInSessionStorage = (id: number, patch: Partial<AllReportDataType>) => {
		const tabs = ['sent', 'received', 'progress', 'active', 'closed'];
		tabs.forEach((tab) => {
			const key = `reportsFeedState_${tab}`;
			const saved = sessionStorage.getItem(key);
			if (saved) {
				try {
					const parsed = JSON.parse(saved);
					if (parsed.AllReportData) {
						let updated = false;
						const newData = parsed.AllReportData.map((r: AllReportDataType) => {
							if (Number(r.id) === id) {
								updated = true;
								return { ...r, ...patch };
							}
							return r;
						});
						if (updated) {
							sessionStorage.setItem(key, JSON.stringify({ ...parsed, AllReportData: newData }));
						}
					}
				} catch (e) {}
			}
		});
	};

	// Auto-sync any local updates to the report (likes, comments, number)
	// back to the feed cache.
	useEffect(() => {
		if (AllReportData && AllReportData.id) {
			patchReportInSessionStorage(AllReportData.id, AllReportData);
		}
	}, [AllReportData]);

	const saveReportNumber = async () => {
		if (!reportId || isSavingNumber) return;
		const next = numberDraft.trim();
		// Nothing changed — just close the editor rather than issuing a write.
		if (next === (AllReportData?.reportNumber || '')) {
			setIsEditingNumber(false);
			return;
		}
		setIsSavingNumber(true);
		try {
			const res = await fetch(`${BACKEND_URL}/api/setReportNumber/${reportId}`, {
				method: 'PATCH',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ number: next }),
			});
			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.message || 'Could not save the report number');
			}
			const result = await res.json();
			set_AllReportData((prev) =>
				prev ? { ...prev, reportNumber: result.reportNumber ?? null } : prev
			);
			setIsEditingNumber(false);
			setToastMessage(result.reportNumber ? 'Report number saved' : 'Report number cleared');
		} catch (e: any) {
			setToastMessage(e?.message || 'Could not save the report number');
		} finally {
			setIsSavingNumber(false);
		}
	};

	// Reporter closes their own report. Only an authority can reopen it, so this
	// is one-way from the reporter's side.
	const handleCloseMyReport = async () => {
		if (!reportId || isClosingReport) return;
		setIsClosingReport(true);
		try {
			const res = await fetch(`${BACKEND_URL}/api/closeMyReport/${reportId}`, {
				method: 'PATCH',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ reason: closeReasonDraft.trim() }),
			});
			if (!res.ok) {
				const err = await res.json().catch(() => ({}));
				throw new Error(err.message || 'Could not close the report');
			}
			const result = await res.json();
			const closedAt = result.closedAt || new Date().toISOString();
			// Update locally (no refetch): the timeline gets the new closed event.
			set_AllReportData((prev) =>
				prev
					? {
							...prev,
							isClosed: true,
							status: 'closed',
							closedAt,
							closedByReporter: true,
							closeReason: result.closeReason || null,
							reportChain: [
								...(prev.reportChain || []),
								{
									kind: 'closed' as const,
									date: closedAt,
									reason: result.closeReason || '',
									actor: {
										kind: 'user' as const,
										id: prev.userId,
										name: prev.name,
										profilepicURL: prev.profilepicURL,
										organisation: null,
										isAnonymous: false,
									},
									target: null,
								},
							],
						}
					: prev
			);
			setShowCloseConfirm(false);
			setCloseReasonDraft('');
			setToastMessage('Report closed');
		} catch (e: any) {
			setToastMessage(e?.message || 'Could not close the report');
		} finally {
			setIsClosingReport(false);
		}
	};

	const handleOpenModal = () => setShowModal(true);
	const handleCloseModal = () => setShowModal(false);

	// Auto-dismiss transient toasts.
	useEffect(() => {
		if (!toastMessage) return;
		const t = setTimeout(() => setToastMessage(null), 3000);
		return () => clearTimeout(t);
	}, [toastMessage]);

	// Clear cached reports feed/scroll state so the previous page reloads fresh
	// (and at the top) when we bounce back from a deleted report.
	const goBackToFreshReports = () => {
		['reportsFeedState_sent', 'reportsFeedState_received', 'reportsScrollPos_sent', 'reportsScrollPos_received'].forEach(
			(key) => sessionStorage.removeItem(key)
		);
		navigate(-1);
	};

	// The report is gone (deleted here or from another device). Show a brief
	// notice, then return to a freshly-loaded reports feed. Guard double-fire.
	const handleReportGone = () => {
		setReportGone((already) => {
			if (!already) {
				setTimeout(() => {
					goBackToFreshReports();
				}, 1200);
			}
			return true;
		});
	};

	useEffect(() => {
		isMountedRef.current = true;
		const handleClickOutside = (event: MouseEvent) => {
			if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
				setSortDropdownOpen(false);
			}
			if (commentDropdownRef.current && !commentDropdownRef.current.contains(event.target as Node)) {
				// If we click on the dots button itself, let the button's own onClick handle the toggle
				if (!(event.target as HTMLElement).closest('.srh-comment-dots')) {
					setOpenCommentDotsId(null);
				}
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			isMountedRef.current = false;
			document.removeEventListener('mousedown', handleClickOutside);
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);



	// Build a direct image URL from the stored media name. (No '.jpg' append:
	// the API's serveMedia resolves extensionless names, and Blob+CDN stores
	// objects under their exact name — appending would 404 there.)
	const buildImageUrl = (mediaName: string): string => {
		return `${MEDIA_BASE_URL}/${mediaName}`;
	};

	useEffect(() => {
		if (!reportId) {
			console.error('ReportId is undefined or not found in route parameters.');
			return;
		}

		const fetchData = async () => {
			if (isLoading) return;
			setIsLoading(true);

			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			abortControllerRef.current = new AbortController();

			// getReport now requires auth (reports are private) — send the bearer
			// token like every other authed call in this component.
			const requestOptions = {
				method: 'GET',
				redirect: 'follow' as RequestRedirect,
				credentials: 'include' as RequestCredentials,
				headers: {
				},
				signal: abortControllerRef.current.signal,
			};

			try {
				const response = await fetch(`${BACKEND_URL}/api/getReport/${reportId}`, requestOptions);
				// Deleted report: 404, or a 200 with an empty/null body from getReport.
				if (response.status === 404) {
					if (isMountedRef.current) handleReportGone();
					return;
				}
				// Private report the viewer isn't allowed to see (reports are scoped
				// to the reporter, the authorities in the chain, and admins).
				if (response.status === 403) {
					if (isMountedRef.current) setAccessDenied(true);
					return;
				}
				if (!response.ok) throw new Error('Failed to fetch report');
				const result = await response.json();

				if (!isMountedRef.current) return;

				if (!result) {
					handleReportGone();
					return;
				}

				const imageUrl = buildImageUrl(result.media[0].internalURL);

				const commonChainFields = {
					forwardHistory: (result.forwardHistory as ForwardHistoryEntry[]) || [],
					originalAuthority: result.originalAuthority || null,
					currentAuthority: result.currentAuthority || null,
					closedByAuthority: result.closedByAuthority || null,
					isClosed: !!result.isClosed,
					closedAt: result.closedAt || null,
					closedByReporter: !!result.closedByReporter,
					closeReason: result.closeReason || null,
					reportChain: (result.reportChain as ChainEvent[]) || [],
					inProgress: !!result.inProgress,
					status: result.status,
					reportNumber: result.reportNumber ?? null,
					canSetReportNumber: !!result.canSetReportNumber,
				};

				const reportData = result.isAnonymous
					? {
						id: reportId ? parseInt(reportId, 10) : 0,
						title: result.title as string,
						content: result.body as string,
						format: result.media[0].format.substring(0, 5),
						image_url: imageUrl,
						name: 'Anonymous',
						location: `${result.media[0].latitude},${result.media[0].longitude}`,
						time: `${result.createdAt.substring(0, 10)} ${result.createdAt.substring(11, 19)}`,
						commentCount: result.commentCount as number,
						comments: result.comments as Comments[],
						country: null,
						state: null,
						city: null,
						profilepicURL: result.user.profilepicURL,
						internalURL: result.media[0]?.internalURL || null,
						requiredFieldValues: result.requiredFieldValues || {},
						isAnonymous: true,
						userId: result.user.id,
						...commonChainFields,
					}
					: {
						id: reportId ? parseInt(reportId, 10) : 0,
						title: result.title as string,
						content: result.body as string,
						format: result.media[0].format.substring(0, 5),
						image_url: imageUrl,
						name: result.user.name as string,
						location: `(${result.media[0].latitude}, ${result.media[0].longitude})`,
						time: `${result.createdAt.substring(0, 10)} ${result.createdAt.substring(11, 19)}`,
						commentCount: result.commentCount as number,
						comments: result.comments as Comments[],
						country: null,
						state: null,
						city: null,
						profilepicURL: result.user.profilepicURL,
						internalURL: result.media[0]?.internalURL || null,
						requiredFieldValues: result.requiredFieldValues || {},
						isAnonymous: false,
						userId: result.user.id,
						...commonChainFields,
					};

				if (isMountedRef.current) {
					set_AllReportData(reportData);
					if (reportId) updatePostMetadata(parseInt(reportId, 10), { commentCount: reportData.commentCount });

					// Fetch location data
					try {
						const geoResult = await reverseGeocode(result.media[0].latitude, result.media[0].longitude);
						if (!isMountedRef.current) return;

						if (isMountedRef.current) {
							set_AllReportData((prevData) => ({
								...prevData!,
								...geoResult,
							}));
						}
					} catch (error) {
						console.error('Error fetching location:', error);
					}
				}
			} catch (error) {
				if (error instanceof Error && error.name === 'AbortError') {
					console.log('Fetch aborted');
				} else {
					console.error('Error fetching data:', error);
				}
			} finally {
				if (isMountedRef.current) {
					setIsLoading(false);
					abortControllerRef.current = null;
				}
			}
		};

		fetchData();

		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reportId, updatePostMetadata]);

	const createComment = async (body: string) => {
		const requestBody = {
			postId: null,
			parentId: CommentParentId !== null ? CommentParentId : null,
			reportId: reportId ? parseInt(reportId, 10) : null, // must be number, not string
			body: CommentBody,
			isAnonymous: false,
		};

		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(requestBody),
			redirect: 'follow' as RequestRedirect,
			credentials: 'include' as RequestCredentials,
		};
		try {
			const response = await fetch(`${BACKEND_URL}/api/createComment`, requestOptions);
			// Report was deleted (here or from another device) — bounce gracefully.
			if (response.status === 404) {
				handleReportGone();
				return;
			}
			if (!response.ok) throw new Error(`Server error: ${response.status}`);
			const result = await response.text();
			const commentId = parseInt(result, 10);
			if (isNaN(commentId)) throw new Error('Invalid comment ID from server');
			const cmt: Comments = {
				commentId,
				parentId: CommentParentId,
				isAnonymous: false,
				user: {
					name: user?.name!,
					id: user?.id!,
					profilepicURL: user?.profilepicURL!,
				},
				likeCount: 0,
				createdAt: `${new Date().toISOString()}`,
				body: body,
			};
			const newComments = [...AllReportData!!.comments, cmt];
			const newCount = AllReportData!!.commentCount + 1;
			set_AllReportData({ ...AllReportData!!, comments: newComments, commentCount: newCount });
			if (reportId) updatePostMetadata(parseInt(reportId, 10), { commentCount: newCount });
			//set comment body to ""
			set_CommentBody('');
			if (CommentParentId !== null) set_CommentParentId(null);
			if (CommentName !== '') set_CommentName('');
		} catch (error: any) {
			console.error('Error occurred while creating comment.', error);
			alert('Failed to post comment: ' + (error.message || String(error)));
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			if (CommentBody.trim() === '') console.log('comment cannot be empty');
			else createComment(CommentBody);
		}
	};

	//   const reactPost = async (type: string) => {

	//     const urlencoded = new URLSearchParams();
	//     urlencoded.append('postId', postId!);
	//     urlencoded.append('type', type);
	// 	urlencoded.append('isAnonymous', user?.name === "Anonymous" ? 'true' : 'false');
	//     const requestOptions = {
	//       method: 'POST',
	//       body: urlencoded,
	//       redirect: 'follow' as RequestRedirect,
	//       credentials: 'include' as RequestCredentials,
	//     };

	//     try {
	//       const response = await fetch('https://backend.b-reporter.com/api/react', requestOptions);
	//       if ((response.status === 200 || response.status === 201)) {
	//         set_AllPostData((prevData) => ({
	//           ...prevData!,
	//           likeCount: type === 'like' ? prevData!.likeCount + 1 : prevData!.likeCount - 1
	//         }));
	//         setIsLiked(type === 'like');
	//       }
	//     } catch (error) {
	//       console.error('Error occurred while reacting.', error);
	//     }
	//   };

	//   const handleLikeClick = () => {
	//     reactPost(isLiked ? 'dislike' : 'like');
	//   };

	const handleDeleteComment = async (commentId: number) => {
		try {
			const requestOptions = {
				method: 'DELETE',
				redirect: 'follow' as RequestRedirect,
				credentials: 'include' as RequestCredentials,
				headers: {
					'Content-Type': 'application/json',
				},
			};
			const res = await fetch(`${BACKEND_URL}/api/deleteComment/${commentId}`, requestOptions);

			// Even if 404 (already deleted), we want to remove it from UI
			if (res.ok || res.status === 404) {
				if (AllReportData) {
					// Function to recursively find direct child replies
					const findReplies = (cId: number, allComments: Comments[]): number[] => {
						const directReplies = allComments.filter((c) => c.parentId === cId).map((c) => c.commentId);
						return [...directReplies, ...directReplies.flatMap((id) => findReplies(id, allComments))];
					};

					const idsToRemove = [commentId, ...findReplies(commentId, AllReportData.comments)];
					const filteredComments = AllReportData.comments.filter((c) => !idsToRemove.includes(c.commentId));
					const newCommentCount = Math.max(0, AllReportData.commentCount - idsToRemove.length);

					set_AllReportData({
						...AllReportData,
						comments: filteredComments,
						commentCount: newCommentCount,
					});

					if (reportId) {
						updatePostMetadata(parseInt(reportId, 10), { commentCount: newCommentCount });
					}
				}
			} else {
				console.error('Failed to delete comment:', await res.text());
			}
		} catch (error) {
			console.error('Error deleting comment:', error);
		}
	};
	const handleDeleteReport = async () => {
		if (!reportId) return;
		setIsDeleting(true);
		try {
			const response = await fetch(`${BACKEND_URL}/api/deleteReport/${reportId}`, {
				method: 'DELETE',
				headers: {
				},
				credentials: 'include',
			});

			// 404 → already deleted elsewhere; treat as success for the user.
			if (response.status === 404) {
				setShowDeleteConfirm(false);
				handleReportGone();
				return;
			}
			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(errorData.message || 'Failed to delete report');
			}

			// Successfully deleted, clear cache and navigate back
			goBackToFreshReports();
		} catch (error: any) {
			console.error('Error deleting report:', error);
			alert(error.message || 'Failed to delete report');
		} finally {
			setIsDeleting(false);
			setShowDeleteConfirm(false);
		}
	};

	const CommentTree = (oldest: boolean = true, comments: Comments[]): JSX.Element => {
		const mp = new Map<number, Comments[]>();
		comments.forEach((val) => {
			if (val.parentId === null) mp.set(val.commentId, [val]);
			else mp.get(val.parentId)?.push(val);
		});

		const keys: number[] = [];
		const sortedMap = new Map<number, Comments[]>();
		mp.forEach((value, key) => keys.push(key));

		if (oldest) {
			keys.sort((a, b) => a - b).forEach((val) => sortedMap.set(val, mp.get(val)!));
		} else {
			keys.sort((a, b) => b - a).forEach((val) => sortedMap.set(val, mp.get(val)!));
		}

		const temp: JSX.Element[] = [];
		sortedMap.forEach((value) => {
			value.forEach((comment) => {
				temp.push(
					<div
						className={`srh-comment ${comment.parentId != null ? 'srh-comment--reply' : ''}`}
						key={comment.commentId}
					>
						<div className='srh-comment-avatar'>
							<ProfileImage
								name={comment.user?.name}
								pfpURL={comment.user?.profilepicURL}
								className='srh-comment-pfp'
								isAnonymous={comment.isAnonymous}
							/>
						</div>
						<div className='srh-comment-bubble'>
							<div className='srh-comment-header'>
								<span className='srh-comment-name'>{comment.user.name}</span>
								<div className='relative'>
									<button
										className='srh-comment-dots'
										title='More options'
										onClick={() =>
											setOpenCommentDotsId(openCommentDotsId === comment.commentId ? null : comment.commentId)
										}
									>
										<svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
											<circle cx='5' cy='12' r='2' />
											<circle cx='12' cy='12' r='2' />
											<circle cx='19' cy='12' r='2' />
										</svg>
									</button>
									{openCommentDotsId === comment.commentId && (
										<div className='srh-comment-dropdown' ref={commentDropdownRef}>
											{comment.user.id === user?.id ? (
												<button
													className='srh-comment-dropdown-item srh-comment-dropdown-item--danger'
													onClick={() => {
														setOpenCommentDotsId(null);
														handleDeleteComment(comment.commentId);
													}}
												>
													Delete
												</button>
											) : (
												<button
													className='srh-comment-dropdown-item'
													onClick={() => {
														setOpenCommentDotsId(null);
														// TODO: Implement Report Action
														console.log('Report comment', comment.commentId);
													}}
												>
													Report
												</button>
											)}
										</div>
									)}
								</div>
							</div>
							<div className='srh-comment-body'>{comment.body}</div>
							<div className='srh-comment-footer'>
								<button
									className='srh-comment-reply-btn'
									onClick={() => {
										set_CommentParentId(comment.parentId == null ? comment.commentId : comment.parentId);
										set_CommentName(comment.user.name);
										setTimeout(() => commentInputRef.current?.focus(), 0);
									}}
								>
									Reply
								</button>
								<span className='srh-comment-time'>{formatCustomDistanceToNow(moment.utc(comment.createdAt).toDate())}</span>
							</div>
						</div>
					</div>
				);
			});
		});

		return <>{temp.map((val) => val)}</>;
	};

	const navigate = useNavigate();

	// Report was deleted (here or from another device). Show a brief notice
	// while we bounce back to a freshly-loaded reports feed.
	if (reportGone) {
		return (
			<div className='srh-page-bg'>
				<div className='min-h-screen flex items-center justify-center p-4'>
					<div className='bg-white rounded-2xl shadow-lg max-w-sm w-full p-8 text-center'>
						<div className='w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
							<AlertCircle className='w-7 h-7 text-red-600' />
						</div>
						<h3 className='text-lg font-bold text-gray-900 mb-2'>This report no longer exists</h3>
						<p className='text-sm text-gray-500'>It may have been deleted. Returning you to the reports feed…</p>
					</div>
				</div>
			</div>
		);
	}

	// The report exists but this account isn't permitted to view it (e.g. the
	// report id in the URL was changed to one that isn't theirs).
	if (accessDenied) {
		return (
			<div className='srh-page-bg'>
				<div className='min-h-screen flex items-center justify-center p-4'>
					<div className='bg-white rounded-2xl shadow-lg max-w-sm w-full p-8 text-center'>
						<div className='w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4'>
							<Lock className='w-7 h-7 text-amber-600' />
						</div>
						<h3 className='text-lg font-bold text-gray-900 mb-2'>You don't have access to this report</h3>
						<p className='text-sm text-gray-500'>
							Reports are private to the person who filed them and the authorities handling them.
						</p>
						<button
							onClick={() => navigate(-1)}
							className='mt-6 inline-flex items-center justify-center rounded-xl bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800'
						>
							Go back
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='srh-page-bg'>
			{/* Transient toast */}
			{toastMessage && (
				<div className='fixed bottom-6 right-6 z-[1100] bg-gray-900 text-white px-5 py-3 rounded-xl text-sm font-semibold shadow-2xl'>
					{toastMessage}
				</div>
			)}
			{!reportId ? (
				<div className='d-flex justify-content-center align-items-center' style={{ height: '70vh' }}>
					<div className='srh-spinner'>
						<span className='spinner-border' role='status' />
						<span>Loading...</span>
					</div>
				</div>
			) : (
				<>
					{AllReportData ? (
						<div>
							<CustomModal show={showModal} handleClose={handleCloseModal} imageUrl={AllReportData.image_url} />

							{/* Outer centering wrapper */}
							<div className='srh-outer'>
								<div className='srh-card'>
									{/* ── HEADER ─────────────────────────────────── */}
									<div className='srh-header'>
										{/* Left: back button + avatar + name */}
										<div className='srh-header-left'>
											<button className='srh-back-btn' onClick={() => navigate(-1)} title='Go back'>
												<svg
													width='20'
													height='20'
													viewBox='0 0 24 24'
													fill='none'
													stroke='currentColor'
													strokeWidth='2.2'
													strokeLinecap='round'
													strokeLinejoin='round'
												>
													<polyline points='15 18 9 12 15 6' />
												</svg>
											</button>
											<div className='srh-avatar'>
												<ProfileImage
													name={AllReportData.name}
													pfpURL={AllReportData.profilepicURL}
													isAnonymous={AllReportData.isAnonymous}
												/>
											</div>
											<span className='srh-sender-name'>{AllReportData.name}</span>
										</div>

										{/* Right: date + location + divider + 3-dots */}
										<div className='srh-header-right'>
											<div className='srh-meta'>
												<div className='srh-meta-row'>
													<span className='srh-meta-label'>Created on:</span>
													<span className='srh-meta-value srh-meta-value-line1'>
														{moment.utc(AllReportData.time).format('DD MMM YYYY, HH:mm')}
														<span className='srh-hide-mobile'> ({formatCustomDistanceToNow(moment.utc(AllReportData.time).toDate())})</span>
													</span>
												</div>
												<div className='srh-meta-row'>
													<span className='srh-meta-label'>Location:</span>
													<div className='srh-meta-value srh-meta-value-line2'>
														<SophisticatedAddress
															parts={AllReportData.address_parts || []}
															locationQuery={AllReportData.location}
														/>
													</div>
												</div>
											</div>
											{/* vertical bar separator */}
											<div className='srh-vbar' />
											{/* 3-dot menu */}
											<button className='srh-dots-btn' title='More options'>
												<svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor'>
													<circle cx='12' cy='5' r='1.5' />
													<circle cx='12' cy='12' r='1.5' />
													<circle cx='12' cy='19' r='1.5' />
												</svg>
											</button>
										</div>
									</div>

									{/* ── IMAGE ──────────────────────────────────── */}
									<div className='srh-image-wrap' onClick={handleOpenModal}>
										{AllReportData.format === 'image' ? (
											<ReportImage className='srh-image bg-gray-100' src={AllReportData.image_url} alt='Report' />
										) : (
											<div className='srh-video-wrap'>
												<Player playsInline fluid={false} height={420} width={undefined}>
													<source src={AllReportData.image_url} type='video/mp4' />
													<BigPlayButton position='center' />
													<ControlBar>
														<PlaybackRateMenuButton rates={[2, 1.5, 1, 0.75, 0.5]} />
													</ControlBar>
												</Player>
											</div>
										)}
									</div>

									{/* ── TITLE + DESCRIPTION ─────────────────────── */}
									<div className='srh-body'>
										{/* Status + the authority's own report number, directly above the
										    title. This is where the number is ASSIGNED (the feed card only
										    displays it), so it sits with the report's identity rather than
										    buried in an action menu. */}
										<div className='mb-2 flex flex-wrap items-center gap-2'>
											{AllReportData.status && (
												<span
													className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
														AllReportData.status === 'closed'
															? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
															: AllReportData.status === 'in_progress'
																? 'bg-amber-50 text-amber-700 border border-amber-200'
																: 'bg-slate-100 text-slate-600 border border-slate-200'
													}`}
												>
													{AllReportData.status === 'closed'
														? 'Closed'
														: AllReportData.status === 'in_progress'
															? 'In progress'
															: 'Received'}
												</span>
											)}

											{isEditingNumber ? (
												<div className='inline-flex items-center gap-1.5'>
													<input
														ref={numberInputRef}
														type='text'
														maxLength={64}
														value={numberDraft}
														autoFocus
														onChange={(e) => setNumberDraft(e.target.value)}
														onKeyDown={(e) => {
															if (e.key === 'Enter') saveReportNumber();
															if (e.key === 'Escape') setIsEditingNumber(false);
														}}
														placeholder='e.g. CR/2026/0412'
														className='w-44 rounded-md border border-indigo-300 bg-white px-2 py-1 font-mono text-xs text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200'
													/>
													<button
														onClick={saveReportNumber}
														disabled={isSavingNumber}
														className='rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60'
													>
														{isSavingNumber ? 'Saving…' : 'Save'}
													</button>
													<button
														onClick={() => setIsEditingNumber(false)}
														disabled={isSavingNumber}
														className='rounded-md bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-200'
													>
														Cancel
													</button>
												</div>
											) : AllReportData.reportNumber ? (
												<button
													onClick={() => {
														if (!AllReportData.canSetReportNumber) return;
														setNumberDraft(AllReportData.reportNumber || '');
														setIsEditingNumber(true);
													}}
													disabled={!AllReportData.canSetReportNumber}
													title={
														AllReportData.canSetReportNumber
															? 'Your bookkeeping number for this report — only you can see it'
															: 'Report number'
													}
													className='inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 font-mono text-xs font-semibold text-indigo-700 transition-colors enabled:hover:bg-indigo-100'
												>
													#{AllReportData.reportNumber}
													{AllReportData.canSetReportNumber && (
														<Pencil size={11} className='opacity-70' />
													)}
												</button>
											) : AllReportData.canSetReportNumber ? (
												<button
													onClick={() => {
														setNumberDraft('');
														setIsEditingNumber(true);
													}}
													title='Record your own number for this report (only you can see it)'
													className='inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-2 py-1 text-xs font-semibold text-gray-500 transition-colors hover:border-indigo-300 hover:text-indigo-600'
												>
													<Hash size={11} />
													Add report number
												</button>
											) : null}

											{/* Reporter-only. Anonymous reports expose userId 0, so never match. */}
											{user?.id != null && user.id === AllReportData.userId && !AllReportData.isClosed && (
												<button
													onClick={() => setShowCloseConfirm(true)}
													title='Mark this report as resolved or no longer needed'
													className='inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100'
												>
													<CheckCircle2 size={12} />
													Close report
												</button>
											)}
										</div>

										<div className='flex justify-between items-start gap-3'>
											{AllReportData.title && <h2 className='srh-title'>{AllReportData.title}</h2>}
											<button
												className='flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors text-sm font-semibold border border-amber-100 shrink-0 mb-2'
												title='Risko-Meter'
												onClick={(e) => {
													e.stopPropagation();
													if (AllReportData.internalURL) {
														setRiskoMeterMediaId(AllReportData.internalURL);
													}
												}}
											>
												<Gauge size={16} />
												<span>Risk</span>
											</button>
										</div>
										<p className={`srh-description ${!AllReportData.content ? 'srh-description--empty' : ''}`}>
											{AllReportData.content || 'No description'}
										</p>

										{/* Required field values (if any) */}
										{AllReportData.requiredFieldValues && Object.keys(AllReportData.requiredFieldValues).length > 0 && (
											<div className='srh-required-fields'>
												{Object.entries(AllReportData.requiredFieldValues).map(([key, value]) => (
													<div key={key} className='srh-field-item'>
														<span className='srh-field-key'>{prettyFieldLabel(key)}:</span>
														<span className='srh-field-value'>{value}</span>
													</div>
												))}
											</div>
										)}
									</div>

									<div className='srh-divider' />

									{/* ── FORWARD CHAIN ──────────────────────────── */}
									<ReportChain data={AllReportData} />

									<div className='srh-divider' />

									{/* ── COMMENT INPUT ───────────────────────────── */}
									{/* ── CUSTOM ACTION BAR (SINGLE LINE) ───────── */}
									<div className='flex flex-nowrap items-center px-4 py-3 gap-3'>
										{/* Compact Comment Input */}
										<div className='flex-1 flex gap-2 min-w-0 bg-gray-50 rounded-full p-1 border border-gray-200 focus-within:border-blue-500 focus-within:ring-0 transition-colors items-center h-10'>
											{CommentName && (
												<div className='bg-blue-900 text-white text-[10px] h-8 pl-3 pr-1 rounded-full flex items-center shrink-0 font-bold'>
													<span className='mb-[0.5px]'>@{CommentName}</span>
													<button
														onClick={() => {
															set_CommentName('');
															set_CommentParentId(null);
														}}
														className='ml-1 w-6 h-6 text-white hover:bg-blue-800 rounded-full flex items-center justify-center transition-colors'
														title='Remove reply'
													>
														<X size={14} strokeWidth={3} />
													</button>
												</div>
											)}
											<input
												ref={commentInputRef}
												type='text'
												className='flex-1 bg-transparent border-none text-sm outline-none focus:outline-none focus:ring-0 px-2 py-0 h-full min-w-0 shadow-none'
												placeholder='Write a comment…'
												value={CommentBody}
												onChange={(e) => set_CommentBody(e.target.value)}
												onKeyDown={handleKeyPress}
											/>
											<button
												className='shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 transition-all disabled:opacity-50 disabled:hover:bg-blue-600 active:scale-95'
												disabled={!CommentBody.trim()}
												onClick={() => {
													if (CommentBody.trim() !== '') createComment(CommentBody);
												}}
											>
												<svg
													width='14'
													height='14'
													viewBox='0 0 24 24'
													fill='none'
													stroke='currentColor'
													strokeWidth='2.5'
													strokeLinecap='round'
													strokeLinejoin='round'
												>
													<line x1='22' y1='2' x2='11' y2='13'></line>
													<polygon points='22 2 15 22 11 13 2 9 22 2'></polygon>
												</svg>
											</button>
										</div>
									</div>

									{/* ── DIVIDER ─────────────────────────────────── */}
									<div className='srh-divider' />

									{/* ── COMMENTS BAR ────────────────────────────── */}
									<div className='srh-comments-bar'>
										<span className='srh-comment-count font-semibold text-sm'>
											Comments ({AllReportData.commentCount})
										</span>
										<div className='srh-sort-row'>
											<span className='srh-sort-label font-medium pr-2'>Sort by</span>
											<div className='relative' ref={sortDropdownRef}>
												<button
													onClick={() => setSortDropdownOpen(!isSortDropdownOpen)}
													className='srh-sort-btn flex items-center justify-between gap-1 w-[100px]'
													type='button'
												>
													<span>{Oldest ? 'Oldest' : 'Newest'}</span>
													<svg
														width='12'
														height='12'
														viewBox='0 0 24 24'
														fill='none'
														stroke='currentColor'
														strokeWidth='2'
														strokeLinecap='round'
														strokeLinejoin='round'
														className={`shrink-0 transition-transform duration-200 ${isSortDropdownOpen ? 'rotate-180' : ''}`}
													>
														<polyline points='6 9 12 15 18 9'></polyline>
													</svg>
												</button>
												{isSortDropdownOpen && (
													<ul className='absolute left-0 top-full mt-1 w-full bg-white border border-gray-100 rounded-md shadow-lg z-20 overflow-hidden list-none p-0 m-0'>
														<li className='w-full'>
															<button
																className='srh-sort-option py-1.5 text-sm text-gray-700 hover:bg-gray-50 font-medium flex justify-center items-center w-full transition-colors'
																onClick={() => {
																	set_Oldest(false);
																	setSortDropdownOpen(false);
																}}
															>
																Newest
															</button>
														</li>
														<li className='w-full border-t border-gray-50'>
															<button
																className='srh-sort-option py-1.5 text-sm text-gray-700 hover:bg-gray-50 font-medium flex justify-center items-center w-full transition-colors'
																onClick={() => {
																	set_Oldest(true);
																	setSortDropdownOpen(false);
																}}
															>
																Oldest
															</button>
														</li>
													</ul>
												)}
											</div>
										</div>
									</div>

									{/* ── COMMENTS LIST ───────────────────────────── */}
									<div className='srh-comments-list pb-8'>
										{CommentTree(Oldest, AllReportData.comments)}
										{AllReportData.comments.length === 0 && (
											<div className='text-center py-8 px-4 text-gray-400 bg-gray-50 rounded-lg mt-2 border border-dashed border-gray-200'>
												<p className='font-medium text-gray-500 mb-1'>No comments yet</p>
												<p className='text-sm'>Be the first to share your thoughts!</p>
											</div>
										)}
									</div>
								</div>
								{/* /.srh-card */}
							</div>
							{/* /.srh-outer */}
						</div>
					) : (
						<div className='d-flex justify-content-center align-items-center' style={{ height: '70vh' }}>
							<div className='srh-spinner'>
								<span className='spinner-border' role='status' />
								<span>Loading...</span>
							</div>
						</div>
					)}
				</>
			)}
			{riskoMeterMediaId && <RiskoMeter mediaId={riskoMeterMediaId} onClose={() => setRiskoMeterMediaId(null)} />}

			{/* Close Report Confirmation Modal */}
			{showCloseConfirm && (
				<div
					className='fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4'
					onClick={() => !isClosingReport && setShowCloseConfirm(false)}
				>
					<div
						className='bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden'
						onClick={(e) => e.stopPropagation()}
					>
						<div className='p-6'>
							<div className='w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4'>
								<CheckCircle2 className='w-8 h-8 text-emerald-600' />
							</div>
							<h3 className='text-xl font-bold text-gray-900 mb-2 text-center'>Close this report?</h3>
							<p className='text-gray-600 text-sm mb-4 text-center'>
								The authority will be told you closed it. Only the authority can reopen it.
							</p>
							<textarea
								value={closeReasonDraft}
								onChange={(e) => setCloseReasonDraft(e.target.value)}
								maxLength={500}
								rows={3}
								placeholder='Reason (optional)'
								disabled={isClosingReport}
								className='w-full rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-900 mb-5 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200'
							/>
							<div className='flex gap-3'>
								<button
									onClick={() => setShowCloseConfirm(false)}
									className='flex-1 px-4 py-2.5 rounded-xl text-gray-700 bg-gray-100 hover:bg-gray-200 font-semibold transition-colors'
									disabled={isClosingReport}
								>
									Cancel
								</button>
								<button
									onClick={handleCloseMyReport}
									className='flex-1 px-4 py-2.5 rounded-xl text-white bg-emerald-600 hover:bg-emerald-700 font-semibold transition-colors flex items-center justify-center gap-2'
									disabled={isClosingReport}
								>
									{isClosingReport ? (
										<div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
									) : (
										'Close report'
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Delete Confirmation Modal */}
			{showDeleteConfirm && (
				<div
					className='fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4'
					onClick={() => !isDeleting && setShowDeleteConfirm(false)}
				>
					<div
						className='bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in duration-200'
						onClick={(e) => e.stopPropagation()}
					>
						<div className='p-6 text-center'>
							<div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
								<AlertCircle className='w-8 h-8 text-red-600' />
							</div>
							<h3 className='text-xl font-bold text-gray-900 mb-2'>Delete Report?</h3>
							<p className='text-gray-600 text-sm mb-6'>
								This action cannot be undone. This report and all its comments will be permanently removed.
							</p>
							<div className='flex gap-3'>
								<button
									onClick={() => setShowDeleteConfirm(false)}
									className='flex-1 px-4 py-2.5 rounded-xl text-gray-700 bg-gray-100 hover:bg-gray-200 font-semibold transition-colors'
									disabled={isDeleting}
								>
									Cancel
								</button>
								<button
									onClick={handleDeleteReport}
									className='flex-1 px-4 py-2.5 rounded-xl text-white bg-red-600 hover:bg-red-700 font-semibold transition-colors flex items-center justify-center gap-2 shadow-lg shadow-red-200'
									disabled={isDeleting}
								>
									{isDeleting ? (
										<div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
									) : (
										'Delete'
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default SingleReportHelper;
